import { CommonModule } from '@angular/common';
import { Component, EventEmitter, forwardRef, Input, Output } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DatePicker } from 'primeng/datepicker';

@Component({
  selector: 'app-shared-datepicker',
  standalone: true,
  imports: [CommonModule, FormsModule, DatePicker],
  template: `
    <p-datepicker
      [ngModel]="innerValue"
      (ngModelChange)="onCalendarModelChange($event)"
      [inputId]="inputId"
      [placeholder]="resolvedPlaceholder"
      [dateFormat]="resolvedDateFormat"
      [minDate]="minDate || null"
      [maxDate]="maxDate || null"
      [disabled]="disabled"
      [readonlyInput]="readonly"
      [required]="required"
      [appendTo]="appendTo"
      [showWeek]="false"
      [panelStyleClass]="'force-below-calendar'"
      [inputStyleClass]="inputStyleClass"
      [style]="{ width: '100%' }"
      (onBlur)="handleBlur($event)"
      size="small">
    </p-datepicker>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SharedDatepickerComponent),
      multi: true
    }
  ]
})
export class SharedDatepickerComponent implements ControlValueAccessor {
  @Input() inputId: string;
  @Input() placeholder?: string;
  @Input() dateFormat?: string;
  @Input() minDate: Date | null = null;
  @Input() maxDate: Date | null = null;
  @Input() disabled = false;
  @Input() readonly = false;
  @Input() required = false;
  @Input() appendTo: any = 'body';
  @Input() inputStyleClass = 'form-control';

  @Output() blur = new EventEmitter<any>();

  innerValue: Date | null = null;

  private onChange: (value: Date | null) => void = () => {};
  private onTouched: () => void = () => {};

  get resolvedPlaceholder(): string {
    if (this.placeholder) {
      return this.placeholder;
    }
    return this.isDobField ? 'MM/DD/YYYY' : 'DD/MMM/YYYY';
  }

  get resolvedDateFormat(): string {
    if (this.dateFormat) {
      return this.dateFormat;
    }
    return this.isDobField ? 'mm/dd/yy' : 'dd/M/yy';
  }

  private get isDobField(): boolean {
    const styleClass = (this.inputStyleClass || '').toLowerCase();
    const inputId = (this.inputId || '').toLowerCase();
    return styleClass.includes('dobinput') || inputId.includes('dob');
  }

  writeValue(value: Date | string | null | undefined): void {
    this.innerValue = this.parseToDate(value);
  }

  registerOnChange(fn: (value: Date | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onCalendarModelChange(value: Date | null): void {
    this.innerValue = value;
    this.onChange(value);
  }

  handleBlur(event: any): void {
    const rawValue = (event?.target?.value || '').toString().trim();
    if (rawValue) {
      const parsed = this.parseToDate(rawValue);
      if (parsed) {
        this.innerValue = parsed;
        this.onChange(parsed);
      }
    }

    this.onTouched();
    this.blur.emit(event);
  }

  private parseToDate(value: Date | string | null | undefined): Date | null {
    if (!value) {
      return null;
    }

    if (value instanceof Date) {
      return isNaN(value.getTime()) ? null : value;
    }

    const parsedFromKnownFormat = this.parseFromKnownFormats(value);
    if (parsedFromKnownFormat) {
      return parsedFromKnownFormat;
    }

    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
  }

  private parseFromKnownFormats(value: string): Date | null {
    const compactEightDigits = /^(\d{2})(\d{2})(\d{4})$/;
    const mmDdYyyy = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
    const ddMmYyyy = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
    const ddMmmYyyy = /^(\d{1,2})\/([a-zA-Z]{3})\/(\d{4})$/;

    const compactMatch = value.match(compactEightDigits);
    if (compactMatch) {
      const first = Number(compactMatch[1]);
      const second = Number(compactMatch[2]);
      const year = Number(compactMatch[3]);

      const month = this.isDobField ? first - 1 : second - 1;
      const day = this.isDobField ? second : first;

      const date = new Date(year, month, day);
      if (
        isNaN(date.getTime()) ||
        date.getFullYear() !== year ||
        date.getMonth() !== month ||
        date.getDate() !== day
      ) {
        return null;
      }
      return date;
    }

    const numericSlashMatch = value.match(mmDdYyyy) || value.match(ddMmYyyy);
    if (numericSlashMatch) {
      const first = Number(numericSlashMatch[1]);
      const second = Number(numericSlashMatch[2]);
      const year = Number(numericSlashMatch[3]);

      const month = this.isDobField ? first - 1 : second - 1;
      const day = this.isDobField ? second : first;

      const date = new Date(year, month, day);
      if (
        isNaN(date.getTime()) ||
        date.getFullYear() !== year ||
        date.getMonth() !== month ||
        date.getDate() !== day
      ) {
        return null;
      }
      return date;
    }

    const ddMmmMatch = value.match(ddMmmYyyy);
    if (ddMmmMatch) {
      const day = Number(ddMmmMatch[1]);
      const monthToken = ddMmmMatch[2].toLowerCase();
      const year = Number(ddMmmMatch[3]);
      const months: { [key: string]: number } = {
        jan: 0,
        feb: 1,
        mar: 2,
        apr: 3,
        may: 4,
        jun: 5,
        jul: 6,
        aug: 7,
        sep: 8,
        oct: 9,
        nov: 10,
        dec: 11
      };

      const month = months[monthToken];
      if (month === undefined) {
        return null;
      }

      const date = new Date(year, month, day);
      return isNaN(date.getTime()) ? null : date;
    }

    return null;
  }
}
